import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DrawerMode, LayoutModule } from "@progress/kendo-angular-layout";
import { RippleModule } from '@progress/kendo-angular-ripple';
import { PermissionScope, UserRole } from '@src/app/shared/enums';
import { NavbarComponent } from './core/components/navbar/navbar.component';
import { UserMenuComponent } from "./core/components/user-menu/user-menu.component";
import { IconName } from './shared/components';

import { filter, lastValueFrom, take, takeWhile, tap, timer } from 'rxjs';
import { MaintenanceModeNotificationComponent } from './core/components/maintenance-mode/maintenance-mode-notification.component';
import { DeactivateDialogComponent } from './shared/components/deactivate-dialog/deactivate-dialog.component';
import { ToggleMenuComponent, ToggleMenuType } from './shared/components/toggle-menu/toggle-menu.component';
import { UserHasAnyScopeDirective } from './shared/directives/user-has-any-scope.directive';
import { CoreUtilsService, NavigationService } from './shared/services';
import { PollingManagerService } from './shared/services/polling-manager/polling-manager.service';
import { ResponsiveService } from './shared/services/responsive.service';
import * as fromStore from './store/conventional';

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    RouterOutlet,
    CommonModule,
    RippleModule,
    ButtonsModule,
    NavbarComponent,
    MaintenanceModeNotificationComponent,
    LayoutModule,
    UserMenuComponent,
    ToggleMenuComponent,
    UserHasAnyScopeDirective,
    DeactivateDialogComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {
  alive = true;
  private enablePolling: boolean = false;
  public enableMaintenanceMode: boolean = false;
  private pollingManager: PollingManagerService = inject(PollingManagerService);
  private responsiveService = inject(ResponsiveService);
  private store = inject(Store<fromStore.State>);
  private cdr = inject(ChangeDetectorRef);
  private _ = inject(NavigationService);
  public utils = inject(CoreUtilsService);

  public expandMode: DrawerMode = "overlay";
  public drawerState = this.responsiveService.drawerState;
  public PermissionScope = PermissionScope;

  public user = toSignal(this.store.select(fromStore.selectUser), { initialValue: undefined });

  public currentRoute = toSignal(
    this.store.select(fromStore.selectRouteUrl),
    { initialValue: '' }
  );
  public inEulaPage = computed(() => {
    return this.currentRoute()
      .toLowerCase()
      .includes('eula')
  });

  public items: Array<{ title: string, url: string }> = [
    { title: "Shipments", url: '/shipments' },
    { title: "Data Sources", url: '/data-sources' },
    { title: "Facilities", url: '/facilities' },
    { title: "Admin", url: '/admin' },
  ];

  public subItemsAdmin: ToggleMenuType = {
    title: 'Admin',
    url: '/admin',
    items: [
      { text: 'Organizations', url: 'organizations', icon: 'organizationProfile' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('adminListOrganizations'), requiredScopes: [PermissionScope.READ_ORGANIZATIONS] },
      { text: 'Organization Profile', url: 'organization', icon: 'organizationProfile' as IconName, cssClass: this.utils.checkFeatureFlagStyle('adminProfile'), isEnable: false, requiredScopes: [PermissionScope.READ_ORGANIZATION] },
      { text: 'Users', url: 'users', icon: 'userManagement' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('adminUser'), requiredScopes: [PermissionScope.READ_USERS] },
      { text: 'Activity', url: 'audits', icon: 'schedule' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('legacyAuditLog'), requiredScopes: [PermissionScope.READ_ORGANIZATION] },
      { text: 'Locations', url: 'locations', icon: 'locationOn' as IconName, cssClass: this.utils.checkFeatureFlagStyle('adminListLocations'), isEnable: false, requiredScopes: [PermissionScope.READ_LOCATIONS] },
      { text: 'Routes', url: 'route-templates', icon: 'routeBuilder' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('routeBuilder'), requiredScopes: [PermissionScope.READ_ROUTE_TEMPLATES] },
      { text: 'Device Configuration', url: 'device-models', icon: 'build' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('deviceConfigurationProfile'), requiredScopes: [PermissionScope.UPDATE_DEVICE_CONFIGURATIONS] },
      { text: 'Rules Builder', url: 'rules-builder', icon: 'rulesBuilder' as IconName, isEnable: false, cssClass: this.utils.checkFeatureFlagStyle('ruleBuilder'), requiredScopes: [PermissionScope.READ_ALERT_RULE_GROUPS] },
    ]
  }

  constructor() {
    const itemList = this.subItemsAdmin.items;
    itemList?.forEach((data, index) => {
      this.checkUserPermission(data!.requiredScopes![0])
        .then(async isPermissionEnabled => {
          try {
            if (!isPermissionEnabled) {
              this.setAdminSubItemEnabled(index, false);
              return;
            }
            //Speficic logic for route templates menu
            //Only shows when the active company is a customer or the user is acting as Command Center.
            if (data!.url === 'route-templates') {
              this.setAdminSubItemEnabled(
                index,
                await this.isRouteTemplatesMenuAllowed()
              );
            } else {
              this.setAdminSubItemEnabled(index, true);
            }
          } catch {
            this.setAdminSubItemEnabled(index, false);
          }
          this.cdr.markForCheck();
        })
        .catch(() => {
          this.setAdminSubItemEnabled(index, false);
          this.cdr.markForCheck();
        });
    });
  }

  /**
   * Updates one admin drawer sub-item visibility by replacing {@link subItemsAdmin} immutably so
   * {@link ToggleMenuComponent}'s signal input sees a new reference and the template refreshes.
   */
  private setAdminSubItemEnabled(index: number, isEnable: boolean): void {
    const prev = this.subItemsAdmin;
    const items = prev.items ?? [];
    this.subItemsAdmin = {
      ...prev,
      items: items.map((item, i) => (i === index ? { ...item, isEnable } : item)),
    };
  }

  /**
   * Whether the Routes (route templates) admin entry should be available for the current session.
   * Waits until the user is loaded so company type and Command Center state are defined, then
   * allows access when the active company is a customer or the user is acting as Command Center.
   */
  private async isRouteTemplatesMenuAllowed(): Promise<boolean> {
    try {
      await lastValueFrom(
        this.store.select(fromStore.selectUserLoaded).pipe(
          filter(loaded => loaded),
          take(1)
        )
      );
       return await lastValueFrom(
         this.store.select(fromStore.selectUserHasAnyRole([
           UserRole.ORGANIZATION_ADMINISTRATOR,
           UserRole.COMMAND_CENTER,
           UserRole.CARGO_SIGNAL_ADMINISTRATOR,
         ])).pipe(take(1))
       );
    } catch (err) {
      console.error('isRouteTemplatesMenuAllowed failed', err);
      return false;
    }
  }

  public async checkUserPermission(permissionScope: PermissionScope) {
    const result = await lastValueFrom(
      this.store.select(fromStore.selectUserHasScope([permissionScope])).pipe(take(2))
    );
    const booleanValue = !!result;
    return booleanValue;
  }

  public onExpandChange(value: boolean): void {
    this.responsiveService.toggleState(value);
  }

  public handleLogout() {
    this.responsiveService.toggleState(false);
    this.store.dispatch(fromStore.logoutConfirmed())
  }

  ngOnInit(): void {
    this.enablePolling = this.utils.getEnvironmentVariable("enablePolling") as boolean;
    this.enableMaintenanceMode = this.utils.getEnvironmentVariable("enableMaintenanceMode") as boolean;
    if (this.enablePolling) {
      this.startPolling();
    } else {
      this.pollingManager.pollAll();
    }
  }

  startPolling() {
    const duration = this.utils.getEnvironmentVariable("pollingDuration") as number;
    timer(0, duration)
      .pipe(
        takeWhile(() => this.alive),
        filter(() => !this.inEulaPage()),
        tap(() => {
          this.pollingManager.pollAll();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
